import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { recordReferralMilestone } from '../utils/referrals';
import AdvancedShareModal from '../components/AdvancedShareModal';
import VerdictMemeFrame from '../components/VerdictMemeFrame';
import VerdictAssetImage from '../components/VerdictAssetImage';
import { resolveDailyVerdictRenderState } from '../utils/dailyVerdictRender';
import { getCanonicalRenderedVerdictAsset } from '../utils/verdictAssets';

const STATUS_RANK = {
  active: 1,
  open: 1,
  closed_pending_verdict: 2,
  closing: 2,
  closed: 3,
  complete: 3,
  completed: 3,
  verdict_ready: 3,
  revealed: 3,
  archived: 3,
};
const TERMINAL_DAILY_STATUSES = new Set(['closed', 'complete', 'completed', 'verdict_ready', 'revealed', 'archived']);
const getStatusRank = (status) => STATUS_RANK[String(status || '').toLowerCase()] || 0;
const PENDING_POLL_BACKOFF_STEPS_MS = [2000, 3000, 5000, 8000];
const PENDING_POLL_MAX_WINDOW_MS = 120000;

function hasDisplayableVerdictPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const winnerIdx = Number(payload.winning_option_index);
  const totalVotes = Number(payload.total_votes);
  const tallies = payload.tallies;
  return Number.isFinite(winnerIdx)
    && Number.isFinite(totalVotes)
    && tallies
    && typeof tallies === 'object';
}

function getDisplayableVerdictPayload(result) {
  if (hasDisplayableVerdictPayload(result?.final_verdict)) return result.final_verdict;
  if (hasDisplayableVerdictPayload(result)) return result;
  return null;
}

function normalizeTallies(tallies, optionCount = 0) {
  const out = {};
  if (tallies && typeof tallies === 'object') {
    Object.entries(tallies).forEach(([key, value]) => {
      const idx = Number(key);
      const count = Number(value);
      if (!Number.isFinite(idx) || idx < 0) return;
      out[idx] = Math.max(0, Number.isFinite(count) ? count : 0);
    });
  }
  for (let i = 0; i < optionCount; i += 1) {
    if (!Number.isFinite(Number(out[i]))) out[i] = 0;
  }
  return out;
}

function buildRenderableVerdict({ status, result, options }) {
  const payload = getDisplayableVerdictPayload(result);
  const statusKey = String(status || '').toLowerCase();
  const optionList = Array.isArray(options) ? options : [];

  if (payload) {
    const tallies = normalizeTallies(payload.tallies, optionList.length);
    const totalVotes = Number(payload.total_votes ?? Object.values(tallies).reduce((a, b) => a + Number(b || 0), 0));
    const winnerIdx = Number.isFinite(Number(payload.winning_option_index)) ? Number(payload.winning_option_index) : 0;
    return {
      ready: true,
      model: {
        winning_option_index: winnerIdx,
        winning_option: optionList[winnerIdx] || payload.winning_option || `Option ${winnerIdx + 1}`,
        total_votes: totalVotes,
        tallies,
        verdict_text: payload.verdict_text || `${optionList[winnerIdx] || `Option ${winnerIdx + 1}`} won with ${totalVotes} vote${totalVotes === 1 ? '' : 's'}.`,
      },
    };
  }

  const normalizedTallies = normalizeTallies(result?.tallies, optionList.length);
  const totalVotes = Number(result?.total_votes ?? Object.values(normalizedTallies).reduce((a, b) => a + Number(b || 0), 0));
  const ranked = Object.entries(normalizedTallies)
    .map(([idx, count]) => ({ idx: Number(idx), count: Number(count || 0) }))
    .filter((r) => Number.isFinite(r.idx))
    .sort((a, b) => (b.count - a.count) || (a.idx - b.idx));
  const explicitWinner = Number(result?.winning_option_index);
  const winnerIdx = Number.isFinite(explicitWinner) ? explicitWinner : (ranked[0]?.idx ?? 0);
  const canRender = TERMINAL_DAILY_STATUSES.has(statusKey) && (Number.isFinite(explicitWinner) || Object.keys(normalizedTallies).length > 0 || totalVotes >= 1);
  if (!canRender) return { ready: false, model: null };

  return {
    ready: true,
    model: {
      winning_option_index: winnerIdx,
      winning_option: optionList[winnerIdx] || `Option ${winnerIdx + 1}`,
      total_votes: totalVotes,
      tallies: normalizedTallies,
      verdict_text: totalVotes === 0
        ? 'No votes were cast. Defaulting to option 1.'
        : `${optionList[winnerIdx] || `Option ${winnerIdx + 1}`} won with ${totalVotes} vote${totalVotes === 1 ? '' : 's'}.`,
    },
  };
}

function hashString(value) {
  const input = String(value || '');
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function pickDeterministicVariant(seed, variants) {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  return variants[hashString(seed) % variants.length];
}

function normalizePromptText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function inferDailyQuestionMode(questionText) {
  const question = normalizePromptText(questionText).toLowerCase();
  if (!question) return 'general';
  if (/^should i\b|^do i\b|^is it worth\b|^can i\b/.test(question)) return 'advice';
  if (/(worst|worse|more annoying|most annoying|more cringe|cringe|more exhausting|exhausting|red flag|rude|cursed|awkward|embarrassing)/.test(question)) {
    return 'negative';
  }
  if (/(best|favorite|better)/.test(question)) return 'positive';
  return 'general';
}

function inferDailyQuestionCategory(questionText, options = []) {
  const haystack = `${normalizePromptText(questionText)} ${options.join(' ')}`.toLowerCase();
  if (/(dinner|lunch|breakfast|snack|drink|meal|pizza|taco|tacos|noodles|trail mix|doritos|protein shake|coffee)/.test(haystack)) {
    return 'food';
  }
  if (/(date|weekend|hang|friends|family|party|costume|captions|reading|watching|movie|movies|social)/.test(haystack)) {
    return 'social';
  }
  if (/(office|meeting|email|reply all|reply-all|circle back|per my last email|coworker|thread|work)/.test(haystack)) {
    return 'work';
  }
  if (/(airport|plane|flight|seat|travel|trip|nashville|chicago|road trip|road-trip|vacation)/.test(haystack)) {
    return 'travel';
  }
  if (/(brand|buy|truck|nike|adidas|cart|checkout|receipt|product|shoe)/.test(haystack)) {
    return 'product';
  }
  return 'general';
}

function joinDailyOptionLabels(labels = []) {
  const items = labels.map((label) => normalizePromptText(label)).filter(Boolean);
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function buildDailySnarkVerdictLine({ question, options, verdictPayload }) {
  const optionList = Array.isArray(options) ? options.filter(Boolean) : [];
  const tallies = normalizeTallies(verdictPayload?.tallies, optionList.length);
  const winnerIdx = Number.isFinite(Number(verdictPayload?.winning_option_index))
    ? Number(verdictPayload.winning_option_index)
    : 0;
  const winner = normalizePromptText(optionList[winnerIdx] || verdictPayload?.winning_option || 'That option');
  const rankedLosers = optionList
    .map((label, idx) => ({ label: normalizePromptText(label), idx, count: Number(tallies[idx] || 0) }))
    .filter((entry) => entry.idx !== winnerIdx)
    .sort((a, b) => (b.count - a.count) || (a.idx - b.idx));
  const runnerUp = rankedLosers[0]?.label || null;
  const loserLabels = rankedLosers.map((entry) => entry.label).filter(Boolean);
  const hasField = loserLabels.length >= 2;
  const loserField = hasField ? joinDailyOptionLabels(loserLabels) : null;
  const opposingCase = hasField ? 'the rest of the field' : runnerUp;
  const mode = inferDailyQuestionMode(question);
  const category = inferDailyQuestionCategory(question, optionList);
  const seed = `${normalizePromptText(question)}|${winner}|${loserField || runnerUp || ''}|${mode}|${category}`;

  if (mode === 'advice') {
    if (/^yes$/i.test(winner)) {
      return pickDeterministicVariant(seed, [
        'Do it. The cautious answer never really had the room.',
        'Do it. Playing it safe was not winning this one.',
        'Do it. The hesitation vote never recovered.',
      ]);
    }
    if (/^no$/i.test(winner)) {
      return pickDeterministicVariant(seed, [
        'Leave it. The room was not buying the pitch.',
        'Skip it. The yes case never really got off the ground.',
        'Leave it. This one was not beating the side-eye.',
      ]);
    }
  }

  if (mode === 'negative') {
    if (category === 'work') {
      return opposingCase
        ? `${opposingCase} is annoying. ${winner} is how the thread gets longer.`
        : `${winner} is how the thread gets longer.`;
    }
    if (category === 'travel') {
      return pickDeterministicVariant(seed, [
        `${winner} won the title nobody wanted.`,
        `${winner} was never beating the allegations.`,
        `${winner} cleared this one for all the wrong reasons.`,
      ]);
    }
    return pickDeterministicVariant(seed, [
      `${winner} won the title nobody wanted.`,
      `${winner} was never beating the allegations.`,
      `${winner} cleared this one for all the wrong reasons.`,
    ]);
  }

  if (category === 'social') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${opposingCase} had a case. ${winner} actually sounds like the better plan.` : `${winner} actually sounds like a weekend.`,
      opposingCase ? `${winner} cleared ${opposingCase}.` : `${winner} had better instincts.`,
      `${winner} cleared this one.`,
    ]);
  }

  if (category === 'food') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${winner} beat ${opposingCase}.` : `${winner} sounds like the better call.`,
      `${winner} was always getting picked first.`,
      opposingCase ? `${opposingCase} was around. ${winner} still cleared.` : `${winner} still cleared.`,
    ]);
  }

  if (category === 'product') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${winner} beat ${opposingCase} on instinct alone.` : `${winner} had better instincts.`,
      `${winner} cleared this one.`,
      opposingCase ? `${opposingCase} made noise. ${winner} made sense.` : `${winner} made sense.`,
    ]);
  }

  if (category === 'travel') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${winner} beat ${opposingCase} because it was the easier answer.` : `${winner} was the easier answer.`,
      `${winner} cleared this one.`,
      opposingCase ? `${opposingCase} made the day harder. ${winner} did not.` : `${winner} did not make the day harder.`,
    ]);
  }

  if (category === 'work') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${winner} beat ${opposingCase} on basic instincts.` : `${winner} had better instincts.`,
      `${winner} cleared this one.`,
      opposingCase ? `${opposingCase} had a case. ${winner} still felt more correct.` : `${winner} still felt more correct.`,
    ]);
  }

  if (mode === 'positive') {
    return pickDeterministicVariant(seed, [
      opposingCase ? `${opposingCase} had a case. ${winner} had better instincts.` : `${winner} had better instincts.`,
      `${winner} cleared this one.`,
      opposingCase ? `${opposingCase} sounded fine. ${winner} sounded right.` : `${winner} sounded right.`,
    ]);
  }

  return pickDeterministicVariant(seed, [
    opposingCase ? `${opposingCase} had a case. ${winner} still cleared.` : `${winner} still cleared.`,
    `${winner} cleared this one.`,
    opposingCase ? `${opposingCase} was around. ${winner} was the answer.` : `${winner} was the answer.`,
  ]);
}

const formatLastUpdated = (ts) => {
  if (!ts) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(ts));
  } catch {
    return null;
  }
};

function DailyDeterministicVerdictHero({
  question,
  verdictText,
  winnerLabel,
  totalVotes,
  secondaryOptions,
}) {
  return (
    <div className="overflow-hidden rounded-[30px] border border-fuchsia-400/20 bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.22),_transparent_32%),radial-gradient(circle_at_80%_20%,_rgba(236,72,153,0.18),_transparent_30%),linear-gradient(180deg,rgba(30,41,59,0.98),rgba(15,23,42,1))] shadow-[0_24px_60px_rgba(0,0,0,0.38)]">
      <div className="border-b border-white/8 px-5 pt-5 pb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-fuchsia-200/75">
          Daily Decision
        </div>
        <div className="mt-3 max-w-[26ch] text-lg font-black leading-tight text-white sm:text-[22px]">
          {question}
        </div>
      </div>

      <div className="px-5 pb-7 pt-7 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Final Verdict</div>
        <div className="mx-auto mt-5 max-w-[12ch] text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-5xl">
          {winnerLabel}
        </div>
        <div className="mx-auto mt-4 max-w-[27ch] text-[15px] font-semibold leading-snug text-fuchsia-100/92 sm:text-base">
          {verdictText}
        </div>
        {secondaryOptions?.length > 0 && (
          <div className="mx-auto mt-5 max-w-[32ch] text-xs leading-relaxed text-slate-400">
            Also in the room: {secondaryOptions.join(' • ')}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-white/8 px-5 py-4 text-xs text-slate-400">
        <span>Locked in</span>
        <span>{Number(totalVotes || 0)} votes</span>
      </div>
    </div>
  );
}

export default function DailyDecisionVerdict() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [decision, setDecision] = useState(null);
  const [result, setResult] = useState(null);
  const [canonicalResult, setCanonicalResult] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [userVote, setUserVote] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBlob, setShareBlob] = useState(null);
  const pollRef = useRef(null);
  const pollAttemptRef = useRef(0);
  const requestIdRef = useRef(0);
  const requestTimeoutRef = useRef(null);
  const [pendingSince, setPendingSince] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const lastRenderLogKeyRef = useRef(null);

  // [DAILY_DECISION_MULTI_OPTION_SELECTION_FIX]
  const normalizeVoteIndex = (voteChoice) => {
    if (voteChoice == null) return null;
    if (voteChoice === 'A') return 0;
    if (voteChoice === 'B') return 1;
    const num = Number(voteChoice);
    return Number.isFinite(num) ? num : null;
  };

  const crewId = searchParams.get('crew');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[daily] verdict_route_open', { decision_id: id, url: window.location.href });
    }
  }, [id]);

  const loadData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    console.log('[daily] fetch_request_started', { request_id: `daily_verdict_${requestId}`, reason: 'verdict_page_load' });
    setLoadError(null);
    if (requestTimeoutRef.current) clearTimeout(requestTimeoutRef.current);
    requestTimeoutRef.current = setTimeout(() => {
      if (requestId !== requestIdRef.current) return;
      console.warn('[daily] fetch_timeout_soft', { request_id: `daily_verdict_${requestId}` });
      setLoadError('Timed out loading verdict. Please retry.');
    }, 12000);

    const { data: dq } = await supabase
      .from('daily_decisions')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (requestId !== requestIdRef.current) {
      console.log('[daily] fetch_response_ignored_stale', { request_id: `daily_verdict_${requestId}` });
      return;
    }
    if (!dq) {
      setDecision(null);
      setLoadError('Decision not found.');
      clearTimeout(requestTimeoutRef.current);
      return;
    }

    const { data: state, error: stateErr } = await supabase.rpc('get_daily_decision_state', {
      p_decision_id: dq.id,
      p_now: new Date().toISOString(),
      p_finalize: true,
    }).maybeSingle();
    if (stateErr) {
      console.warn('[daily] get_state_failed', { decision_id: dq.id, error: stateErr.message });
      setLoadError('Unable to refresh verdict state. Showing last known data.');
    }

    const normalizedStatus =
      state?.status === 'open'
        ? 'active'
        : state?.status === 'closing'
          ? 'closed_pending_verdict'
          : state?.status;
    let effectiveTallies = state?.tallies && typeof state.tallies === 'object' ? state.tallies : {};
    let effectiveTotalVotes = Number(state?.total_votes || 0);
    const statusKey = String(normalizedStatus || '').toLowerCase();
    const canUseLiveVotes = statusKey === 'active' || statusKey === 'open' || statusKey === 'closed_pending_verdict' || statusKey === 'closing';
    if (canUseLiveVotes) {
      const { data: liveVotes } = await supabase
        .from('daily_decision_votes')
        .select('option_index')
        .eq('decision_id', dq.id);
      const liveTallies = {};
      for (const voteRow of liveVotes || []) {
        const idx = Number(voteRow?.option_index);
        if (!Number.isFinite(idx) || idx < 0) continue;
        const key = String(Math.floor(idx));
        liveTallies[key] = (liveTallies[key] || 0) + 1;
      }
      const liveTotal = Object.values(liveTallies).reduce((acc, value) => acc + Number(value || 0), 0);
      if (liveTotal > effectiveTotalVotes) {
        effectiveTallies = liveTallies;
        effectiveTotalVotes = liveTotal;
      }
    }
    const nextDecision = {
      ...dq,
      status: normalizedStatus || dq.status,
      closes_at: state?.closes_at || dq.closes_at,
    };
    const closesAtRaw = state?.closes_at || dq.closes_at || null;
    const closesAtEt = closesAtRaw
      ? new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour12: true,
      }).format(new Date(closesAtRaw))
      : null;
    const nowEt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour12: true,
    }).format(new Date());
    const debugTallies = state?.final_verdict?.tallies || state?.tallies || {};
    console.log('[daily] ensure_state_parsed', {
      decision_id: dq.id,
      status: nextDecision.status || null,
      closes_at_raw: closesAtRaw,
      closes_at_et: closesAtEt,
      now_et: nowEt,
      total_votes: effectiveTotalVotes,
      tallies_keys: Object.keys(effectiveTallies || debugTallies || {}),
      has_verdict_payload: hasDisplayableVerdictPayload(state?.final_verdict),
    });
    setDecision((prev) => {
      if (!prev || prev.id !== nextDecision.id) return nextDecision;
      const prevRank = getStatusRank(prev.status);
      const nextRank = getStatusRank(nextDecision.status);
      if (nextRank < prevRank) {
        console.warn('[daily] stale_response_dropped', {
          decision_id: nextDecision.id,
          prev_status: prev.status || null,
          next_status: nextDecision.status || null,
        });
        return { ...nextDecision, status: prev.status, closes_at: prev.closes_at || nextDecision.closes_at };
      }
      return nextDecision;
    });

    const hasVerdictPayload = hasDisplayableVerdictPayload(state?.final_verdict);
    console.log('[daily] state_applied', {
      decision_id: dq.id,
      status: nextDecision.status || null,
      closes_at: state?.closes_at || dq.closes_at || null,
      revealed_at: dq?.revealed_at || null,
      verdict_source: state?.verdict_source || null,
      has_verdict_payload: hasVerdictPayload,
    });

    if (hasVerdictPayload) {
      const authoritativeResult = {
        decision_id: dq.id,
        tallies: effectiveTallies,
        total_votes: effectiveTotalVotes,
        winning_option_index: Number(state.final_verdict?.winning_option_index ?? 0),
        final_verdict: state.final_verdict,
        verdict_source: state.verdict_source || null,
        reason_code: state.reason_code || null,
        last_updated_at: state.last_updated_at || null,
        meme_status: state.meme_status || null,
        meme_image_url: state.meme_image_url || null,
        share_card_url: state.share_card_url || null,
        composed_share_card_url: state.composed_share_card_url || null,
        background_image_url: state.background_image_url || null,
        final_image_url: state.final_image_url || null,
        media_asset_url: state.media_asset_url || null,
        meme_snark_text: state.meme_snark_text || null,
        meme_error: state.meme_error || null,
      };
      setResult(authoritativeResult);
      console.log('[daily] result_set', {
        decision_id: dq.id,
        status: nextDecision.status || null,
        source: 'authoritative',
        verdict_source: authoritativeResult.verdict_source || null,
        reason_code: authoritativeResult.reason_code || null,
      });
    } else if (TERMINAL_DAILY_STATUSES.has(String(nextDecision.status || '').toLowerCase())) {
      console.warn('[daily] terminal_without_authoritative_final_verdict', {
        decision_id: dq.id,
        status: nextDecision.status || null,
        verdict_source: state?.verdict_source || null,
      });
      setResult((prev) => (hasDisplayableVerdictPayload(prev?.final_verdict || prev) ? prev : null));
    } else {
      const { data: resRow } = await supabase
        .from('daily_decision_results')
        .select('*')
        .eq('decision_id', id)
        .maybeSingle();
      setResult(resRow || null);
    }

    const { data: canonicalRow } = await supabase
      .from('daily_decision_results')
      .select('*')
      .eq('decision_id', id)
      .maybeSingle();
    setCanonicalResult(canonicalRow || null);

    const { data: auth } = await supabase.auth.getUser();
    const authId = auth?.user?.id;
    if (authId && id) {
      const { data: decisionVote } = await supabase
        .from('daily_decision_votes')
        .select('option_index')
        .eq('decision_id', id)
        .eq('user_id', authId)
        .maybeSingle();
      if (decisionVote?.option_index != null) {
        setUserVote(Number(decisionVote.option_index));
      } else {
        const { data: voteRow } = await supabase
          .from('daily_question_votes')
          .select('vote_choice')
          .eq('daily_question_id', id)
          .eq('user_id', authId)
          .eq('crew_id', crewId || null)
          .maybeSingle();
        setUserVote(normalizeVoteIndex(voteRow?.vote_choice));
      }
    }

    setLastUpdatedAt(new Date().toISOString());
    clearTimeout(requestTimeoutRef.current);
    console.log('[daily] fetch_applied', {
      request_id: `daily_verdict_${requestId}`,
      decision_id: dq.id,
    });
  }, [id, crewId]);

  // [DAILY_DECISION_POLL_PENDING]
  useEffect(() => {
    if (!id) return;
    loadData();
  }, [id, crewId, loadData]);

  useEffect(() => {
    return () => {
      if (requestTimeoutRef.current) clearTimeout(requestTimeoutRef.current);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!decision?.id) return;

    const statusKey = String(decision.status || '').toLowerCase();
    const isOpen = statusKey === 'active' || statusKey === 'open';
    const dailyVerdictSource = {
      ...(canonicalResult || {}),
      ...(result || {}),
      final_verdict: result?.final_verdict || canonicalResult?.final_verdict || null,
    };
    const dailyRenderState = resolveDailyVerdictRenderState(dailyVerdictSource, decision);
    const resolvedVerdict = buildRenderableVerdict({
      status: decision?.status,
      result: dailyVerdictSource,
      options: Array.isArray(decision?.options) ? decision.options : [decision?.option_a, decision?.option_b].filter(Boolean),
    });
    const hasResolvedVerdict = dailyRenderState.verdictReady || resolvedVerdict.ready;
    const shouldPoll = !hasResolvedVerdict && (!isOpen || TERMINAL_DAILY_STATUSES.has(statusKey));

    if (shouldPoll) {
      const started = pendingSince || Date.now();
      if (!pendingSince) setPendingSince(started);
      if (Date.now() - started > PENDING_POLL_MAX_WINDOW_MS) {
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = null;
        return;
      }

      if (pollRef.current) clearTimeout(pollRef.current);
      const backoffIdx = Math.min(pollAttemptRef.current, PENDING_POLL_BACKOFF_STEPS_MS.length - 1);
      const nextDelay = PENDING_POLL_BACKOFF_STEPS_MS[backoffIdx];
      console.log('[daily] daily_poll_schedule', { decision_id: decision.id, attempt: pollAttemptRef.current + 1, delay_ms: nextDelay });
      pollRef.current = setTimeout(() => {
        console.log('[daily] daily_poll_tick', { decision_id: decision.id, attempt: pollAttemptRef.current + 1 });
        pollAttemptRef.current += 1;
        loadData();
      }, nextDelay);
    } else {
      setPendingSince(null);
      pollAttemptRef.current = 0;
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    }

    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = null;
    };
  }, [decision?.id, decision?.closes_at, decision?.status, decision?.options, decision?.option_a, decision?.option_b, canonicalResult, result, loadData, pendingSince]); // eslint-disable-line react-hooks/exhaustive-deps -- decision object intentionally excluded

  useEffect(() => {
    if (!id) return;
    if (window.trackEvent) {
      const source = searchParams.get('source') || (crewId ? 'crew_share' : 'other');
      window.trackEvent('dq_verdict_opened', { decision_id: id, crew_id: crewId || null, source });
    }
  }, [id, crewId, searchParams]);

  useEffect(() => {
    if (!id || !crewId) return;
    const loadPulse = async () => {
      const { data } = await supabase.functions.invoke('get-daily-question-pulse', {
        body: { daily_question_id: id, crew_id: crewId },
      });
      setPulse(data || null);
      if (data?.ok && window.trackEvent) {
        window.trackEvent('dq_pulse_viewed', { daily_question_id: id, crew_id: crewId });
      }
    };
    loadPulse();
  }, [id, crewId]);

  const handleVote = async (choiceIdx) => {
    const { data: auth } = await supabase.auth.getUser();
    const authId = auth?.user?.id;
    if (!authId) return;
    const { data: voteResp, error: voteErr } = await supabase.functions.invoke('cast-daily-vote', {
      body: {
        decision_id: id,
        option_index: choiceIdx,
        crew_id: crewId || null,
      },
    });
    if (voteErr || voteResp?.ok === false) {
      console.warn('[daily] verdict_vote_error', {
        decision_id: id,
        option_index: choiceIdx,
        error: voteErr?.message || voteResp?.error || 'vote_failed',
      });
      return;
    }
    try {
      await recordReferralMilestone('first_vote_completed');
    } catch (milestoneErr) {
      console.warn('⚠️ Failed to record referral first_vote_completed milestone:', milestoneErr?.message || milestoneErr);
    }
    setUserVote(choiceIdx);
    await loadData();
  };

  const handleShare = async () => {
    if (!memeImageUrl) return;
    const resp = await fetch(memeImageUrl);
    const blob = await resp.blob();
    setShareBlob(blob);
    setShareOpen(true);
  };

  const options = useMemo(
    () => (Array.isArray(decision?.options) ? decision.options : [decision?.option_a, decision?.option_b].filter(Boolean)),
    [decision?.options, decision?.option_a, decision?.option_b]
  );

  const isOpen = decision?.closes_at && new Date(decision.closes_at).getTime() > Date.now();
  const dailyVerdictSource = useMemo(
    () => ({
      ...(canonicalResult || {}),
      ...(result || {}),
      final_verdict: result?.final_verdict || canonicalResult?.final_verdict || null,
    }),
    [canonicalResult, result]
  );
  const verdictRender = useMemo(() => buildRenderableVerdict({
    status: decision?.status,
    result: dailyVerdictSource,
    options,
  }), [decision?.status, dailyVerdictSource, options]);
  const verdictPayload = verdictRender.model;
  const verdictReady = verdictRender.ready;
  const pendingDelayed = !!pendingSince && (Date.now() - pendingSince) >= 120000;
  const dailyRenderState = useMemo(
    () => resolveDailyVerdictRenderState(dailyVerdictSource, decision),
    [dailyVerdictSource, decision]
  );
  const canonicalDailyAssetUrl = getCanonicalRenderedVerdictAsset({ ...(dailyVerdictSource || {}), ...(decision || {}) }).url;
  const memeImageUrl = dailyRenderState.imageUrl;
  const memeLine = dailyRenderState.text || verdictPayload?.verdict_text || null;
  const hasResolvedDailyVerdict = Boolean(verdictReady || dailyRenderState.verdictReady);
  const showResolvedVerdict = hasResolvedDailyVerdict;
  const isFailed = dailyRenderState.verdictFailed;
  const verdictRenderState = dailyRenderState.renderState;
  const hasEnhancedDailyAsset = useMemo(() => {
    const imageSource = String(
      dailyVerdictSource?.image_source ||
      result?.image_source ||
      canonicalResult?.image_source ||
      ''
    ).toLowerCase();
    return Boolean(
      imageSource === 'ai_primary' ||
      dailyVerdictSource?.composed_share_card_url ||
      result?.composed_share_card_url ||
      canonicalResult?.composed_share_card_url
    );
  }, [dailyVerdictSource, result, canonicalResult]);
  const enhancedDailyAssetUrl = hasEnhancedDailyAsset ? (canonicalDailyAssetUrl || memeImageUrl) : null;
  const showEnhancedDailyHero = Boolean(showResolvedVerdict && enhancedDailyAssetUrl);
  const secondaryDailyOptions = useMemo(() => {
    const tallies = normalizeTallies(verdictPayload?.tallies, options.length);
    const winnerIdx = Number.isFinite(Number(verdictPayload?.winning_option_index))
      ? Number(verdictPayload.winning_option_index)
      : 0;
    return options
      .map((label, idx) => ({ label, idx, count: Number(tallies[idx] || 0) }))
      .filter((entry) => entry.idx !== winnerIdx && entry.label)
      .sort((a, b) => (b.count - a.count) || (a.idx - b.idx))
      .map((entry) => entry.label);
  }, [options, verdictPayload]);
  const dailySnarkLine = useMemo(() => buildDailySnarkVerdictLine({
    question: decision?.question || decision?.prompt || '',
    options,
    verdictPayload,
  }), [decision?.question, decision?.prompt, options, verdictPayload]);

  useEffect(() => {
    if (!decision?.id) return;
    const logPayload = {
      decision_id: decision.id,
      status: decision.status || null,
      is_open: isOpen,
      verdict_ready: hasResolvedDailyVerdict,
      has_verdict_model: Boolean(verdictPayload),
      total_votes: Number(verdictPayload?.total_votes ?? 0),
      winner_index: verdictPayload?.winning_option_index ?? null,
      options_count: options.length,
      meme_status: dailyVerdictSource?.meme_status || null,
      has_meme_image: Boolean(memeImageUrl),
      render_state: verdictRenderState,
    };
    const key = JSON.stringify(logPayload);
    if (lastRenderLogKeyRef.current === key) return;
    lastRenderLogKeyRef.current = key;
    console.log('[daily] verdict_page_render_state', logPayload);
  }, [decision?.id, decision?.status, isOpen, hasResolvedDailyVerdict, verdictPayload, options.length, dailyVerdictSource?.meme_status, memeImageUrl, verdictRenderState]);

  useEffect(() => {
    if (!decision?.id) return;
    console.log('[daily_verdict_render_state]', {
      decision_id: decision.id,
      meme_status: dailyVerdictSource?.meme_status || null,
      has_image: Boolean(memeImageUrl),
      has_text: Boolean(memeLine),
    });
  }, [decision?.id, dailyVerdictSource?.meme_status, memeImageUrl, memeLine]);

  useEffect(() => {
    if (!decision?.id) return;
    console.log('[daily_asset_selection]', {
      decision_id: decision.id,
      meme_status: dailyVerdictSource?.meme_status || null,
      meme_image_url: dailyVerdictSource?.meme_image_url || null,
      share_card_url: dailyVerdictSource?.share_card_url || null,
      final_image_url: dailyVerdictSource?.final_image_url || null,
      background_image_url: dailyVerdictSource?.background_image_url || null,
      media_asset_url: dailyVerdictSource?.media_asset_url || null,
      selected_asset_url: canonicalDailyAssetUrl || null,
      screen: 'detail-daily',
    });
  }, [decision?.id, dailyVerdictSource?.meme_status, dailyVerdictSource?.meme_image_url, dailyVerdictSource?.share_card_url, dailyVerdictSource?.final_image_url, dailyVerdictSource?.background_image_url, dailyVerdictSource?.media_asset_url, canonicalDailyAssetUrl]);

  useEffect(() => {
    if (!decision?.id) return;
    if (dailyRenderState.renderState !== 'ready' && dailyRenderState.hasRenderable) {
      console.warn('[daily_detail_render_mismatch]', {
        decision_id: decision.id,
        meme_status: dailyVerdictSource?.meme_status || null,
        has_image: Boolean(canonicalDailyAssetUrl),
        has_text: Boolean(dailyVerdictSource?.meme_snark_text),
      });
    }
  }, [decision?.id, dailyRenderState.renderState, dailyRenderState.hasRenderable, dailyVerdictSource?.meme_status, canonicalDailyAssetUrl, dailyVerdictSource?.meme_snark_text]);

  if (!decision) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Link to="/verdict-vault" className="text-sm text-slate-300">Back to Vault</Link>
          <div className="mt-6 text-slate-400">{loadError || 'Loading Daily Decision...'}</div>
          {loadError && (
            <div className="mt-2 text-xs text-slate-500">If the link is old, try the latest Daily Decision.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/verdict-vault" className="text-sm text-slate-300">Back to Vault</Link>
        <h1 className="text-2xl font-black mt-4">Daily Decision</h1>
        <div className="text-slate-400 text-sm mt-1">{decision.question || decision.prompt}</div>
        {lastUpdatedAt && (
          <div className="text-[11px] text-slate-500 mt-2">Updated {formatLastUpdated(lastUpdatedAt)}</div>
        )}

        {isOpen && !hasResolvedDailyVerdict && (
          <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-sm font-bold text-purple-200">Vote now</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {options.map((opt, idx) => (
                <button
                  key={`opt-${idx}`}
                  onClick={() => handleVote(idx)}
                  className={`rounded-lg px-4 py-2 text-sm font-bold text-white transition-all ${
                    userVote === idx
                      ? 'bg-green-600 border border-green-400/50'
                      : 'bg-purple-600 hover:bg-purple-500'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {showResolvedVerdict && (
          <div className="mt-6 space-y-4">
            <DailyDeterministicVerdictHero
              question={decision?.question || decision?.prompt || 'Daily Decision'}
              verdictText={dailySnarkLine || verdictPayload?.verdict_text || 'Final verdict is in.'}
              winnerLabel={options[verdictPayload?.winning_option_index ?? 0] || 'Winner'}
              totalVotes={Number(verdictPayload?.total_votes || result?.total_votes || 0)}
              secondaryOptions={secondaryDailyOptions}
            />

            {verdictPayload && (
              <div className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
                <div className="text-sm font-bold text-slate-100">Vote Breakdown</div>
                <div className="text-xs text-slate-400 mt-1">
                  Total votes: {Number(verdictPayload?.total_votes || result?.total_votes || 0)}
                </div>
                <div className="mt-4 space-y-3">
                  {options.map((opt, idx) => {
                    const count = Number(verdictPayload?.tallies?.[idx] ?? result?.tallies?.[idx] ?? 0);
                    const totalVotes = Number(verdictPayload?.total_votes ?? result?.total_votes ?? 0);
                    const pct = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
                    const isWinner = idx === (verdictPayload?.winning_option_index ?? 0);
                    return (
                      <div key={`result-${idx}`}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className={isWinner ? 'font-semibold text-emerald-200' : 'text-slate-300'}>{opt}</span>
                          <span className={isWinner ? 'font-semibold text-emerald-200' : 'text-slate-300'}>{pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-700">
                          <div
                            className={`h-2 rounded-full ${isWinner ? 'bg-emerald-400' : 'bg-purple-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {showEnhancedDailyHero && (
              <div className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900">
                <div className="border-b border-slate-700 px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Daily Showcase</div>
                </div>
                <VerdictAssetImage
                  imageUrl={enhancedDailyAssetUrl}
                  assetSource={{ ...(dailyVerdictSource || {}), ...(decision || {}) }}
                  screen="detail-daily"
                  trialId={decision?.id}
                  alt={decision?.question || 'Daily Decision'}
                  className="block w-full aspect-square object-cover"
                />
                <div className="p-4">
                  <button
                    onClick={handleShare}
                    className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-bold text-white hover:bg-purple-600"
                  >
                    Share
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!isOpen && !hasResolvedDailyVerdict && (
          <div className="mt-6 rounded-2xl overflow-hidden border border-slate-700 bg-slate-900">
            <VerdictMemeFrame
              id={decision?.id}
              title={decision?.question || 'Daily Decision'}
              sideA={options[0]}
              sideB={options[1]}
              verdictLine={null}
              imageUrl={null}
              assetSource={{ ...(dailyVerdictSource || {}), ...(decision || {}) }}
              winnerLabel={options[verdictPayload?.winning_option_index ?? 0] || 'Winner'}
              loserLabel={options[(verdictPayload?.winning_option_index ?? 0) === 0 ? 1 : 0] || 'Runner-up'}
              isDaily
              heightClass="h-64"
              showEmoji={false}
              renderState={isFailed ? 'failed' : 'processing'}
              surface="detail-daily"
            />
            <div className="p-4">
              <div className="text-xs text-slate-400 mt-1">
                {loadError
                  ? loadError
                  : isFailed
                    ? 'We will retry shortly. You can also retry now.'
                    : pendingDelayed
                      ? 'Verdict is delayed. We are retrying automatically.'
                      : 'Verdict is being prepared.'}
              </div>
              <button
                onClick={loadData}
                className="mt-3 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
              >
                Retry now
              </button>
            </div>
          </div>
        )}

        {pulse?.ok && (
          <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-sm font-bold text-purple-200">Crew Pulse</div>
            <div className="text-xs text-slate-400 mt-1">
              {pulse.total_votes} / {pulse.crew_members} voted
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-300">Option A: {pulse.option_a_pct}%</div>
              <div className="h-2 rounded-full bg-slate-700">
                <div className="h-2 rounded-full bg-purple-500" style={{ width: `${pulse.option_a_pct}%` }} />
              </div>
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-300">Option B: {pulse.option_b_pct}%</div>
              <div className="h-2 rounded-full bg-slate-700">
                <div className="h-2 rounded-full bg-purple-500" style={{ width: `${pulse.option_b_pct}%` }} />
              </div>
            </div>
            {pulse.user_has_voted && pulse.user_choice && (
              <div className="mt-2 text-xs text-slate-200">
                You voted: {pulse.user_choice}
              </div>
            )}
          </div>
        )}

        {shareOpen && shareBlob && (
          <AdvancedShareModal
            imageBlob={shareBlob}
            trial={{
              id,
              title: decision.question || decision.prompt,
              sideA: options[0] || 'Option A',
              sideB: options[1] || 'Option B',
              totalVotes: result?.total_votes || 0
            }}
            verdict={{
              winner: (result?.winning_option_index ?? 0) === 0 ? 'A' : 'B',
              winnerPct: result?.total_votes
                ? Math.round(((result?.tallies?.[result?.winning_option_index] || 0) / result.total_votes) * 100)
                : 50
            }}
            onClose={() => setShareOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
